import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../utils/api";
import {
  formatIndianCurrency,
  formatIndianDateTime,
} from "../../utils/formatters";
import { ArrowUpRight, CheckCircle, XCircle, Clock } from "lucide-react";

const AdminWithdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  // per-withdrawal payment reference map
  const [paymentRefs, setPaymentRefs] = useState({});

  useEffect(() => {
    fetchWithdrawals();
  }, [filter]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/withdrawals", {
        params: filter !== "all" ? { status: filter } : {},
      });
      setWithdrawals(res.data || []);
      console.log("Fetched withdrawals:", res.data);
    } catch (err) {
      console.error("Failed to fetch withdrawals:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId, status) => {
    setProcessing(withdrawalId);

    const currentRef = (paymentRefs[withdrawalId] || "").trim();

    // Confirm before rejecting
    if (status === "rejected") {
      const ok = window.confirm(
        "Are you sure you want to reject this withdrawal?"
      );
      if (!ok) {
        setProcessing(null);
        return;
      }
    }

    try {
      await api.post(`/admin/withdrawals/${withdrawalId}/approve`, {
        status,
        remarks: "",
        paymentReference:
          status === "approved" && currentRef ? currentRef : undefined,
      });

      alert(`Withdrawal ${status} successfully!`);

      // clear that row's ref
      setPaymentRefs((prev) => ({
        ...prev,
        [withdrawalId]: "",
      }));

      await fetchWithdrawals();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || `Failed to ${status} withdrawal`);
    } finally {
      setProcessing(null);
    }
  };

  const renderStatusChip = (status) => {
    const base = "px-3 py-1 rounded-full text-xs font-semibold";
    switch (status) {
      case "paid":
        return `${base} bg-success-light text-success`;
      case "approved":
        return `${base} bg-primary-light text-primary`;
      case "rejected":
        return `${base} bg-error-light text-error`;
      default:
        return `${base} bg-warning-light text-warning`;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900">
              Withdrawal Requests
            </h1>
            <p className="text-gray-600 mt-1">{withdrawals.length} requests</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {["pending", "approved", "paid", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Withdrawals List */}
        <div className="space-y-4">
          {withdrawals.length > 0 ? (
            withdrawals.map((withdrawal) => {
              const beneficiary = withdrawal.beneficiary || {};
              const isPending = withdrawal.status === "pending";
              const refValue = paymentRefs[withdrawal.id] || "";
              const isUPI = beneficiary.type === "UPI";

              return (
                <div
                  key={withdrawal.id}
                  className="bg-white rounded-lg border border-gray-200 p-6"
                >
                  {/* Top row: user and status */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {withdrawal.userName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {withdrawal.userEmail}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatIndianDateTime(withdrawal.createdAt)}
                      </p>
                    </div>
                    <span className={renderStatusChip(withdrawal.status)}>
                      {withdrawal.status.toUpperCase()}
                    </span>
                  </div>

                  {/* Amount + type + balances */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Type</p>
                      <p className="font-semibold text-gray-900 capitalize">
                        {withdrawal.type}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Amount</p>
                      <p className="text-lg font-mono font-bold text-primary">
                        {formatIndianCurrency(withdrawal.amount)}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">
                        Actual Balance
                      </p>
                      <p className="font-mono text-sm text-gray-900">
                        {formatIndianCurrency(withdrawal.actualBalance || 0)}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Withdrawable</p>
                      <p className="font-mono text-sm text-gray-900">
                        {formatIndianCurrency(
                          withdrawal.withdrawableBalance || 0
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Beneficiary block – BANK vs UPI aware */}
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Beneficiary identity */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Beneficiary</p>
                      {beneficiary.name ? (
                        <>
                          <p className="text-sm font-semibold text-gray-900">
                            {beneficiary.name}
                          </p>
                          <p className="mt-1 inline-flex items-center gap-1 text-[11px]">
                            <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-800 font-medium">
                              {beneficiary.type || "UNKNOWN"}
                            </span>
                            <span className="text-gray-500">
                              ID: {withdrawal.beneficiaryId}
                            </span>
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-gray-600 mb-1">
                            Beneficiary ID
                          </p>
                          <p className="font-mono text-xs text-gray-900 truncate">
                            {withdrawal.beneficiaryId}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Second column – UPI ID or Bank name */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      {isUPI ? (
                        <>
                          <p className="text-xs text-gray-600 mb-1">UPI ID</p>
                          {beneficiary.upiId ? (
                            <p className="font-mono text-sm text-gray-900">
                              {beneficiary.upiId}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500">
                              No UPI ID found
                            </p>
                          )}
                          <p className="mt-1 text-[11px] text-gray-600">
                            Payout will be sent to this UPI handle.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-gray-600 mb-1">Bank</p>
                          {beneficiary.bankName ? (
                            <>
                              <p className="text-sm font-semibold text-gray-900">
                                {beneficiary.bankName}
                              </p>
                              <p className="text-xs text-gray-600">
                                {beneficiary.branchName || ""}
                              </p>
                            </>
                          ) : (
                            <p className="text-xs text-gray-500">
                              Bank details not available
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Third column – account/IFSC for BANK, meta for UPI */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      {isUPI ? (
                        <>
                          <p className="text-xs text-gray-600 mb-1">
                            Verification
                          </p>
                          <p className="text-xs text-gray-700">
                            Status:{" "}
                            <span className="font-semibold">
                              {beneficiary.verified
                                ? "Verified"
                                : "Not verified"}
                            </span>
                          </p>
                          <p className="mt-1 text-[11px] text-gray-500">
                            Double-check UPI ID before approving large payouts.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-gray-600 mb-1">
                            Account / IFSC
                          </p>
                          {beneficiary.accountNumber ? (
                            <>
                              <p className="font-mono text-xs text-gray-900 truncate">
                                {beneficiary.accountNumber}
                              </p>
                              <p className="font-mono text-xs text-gray-700">
                                {beneficiary.ifsc}
                              </p>
                            </>
                          ) : (
                            <p className="text-xs text-gray-500">
                              Account / IFSC not available
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Pending actions */}
                  {isPending && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Payment Reference (for approval)
                        </label>
                        <input
                          type="text"
                          value={refValue}
                          onChange={(e) =>
                            setPaymentRefs((prev) => ({
                              ...prev,
                              [withdrawal.id]: e.target.value,
                            }))
                          }
                          placeholder="Enter UTR / Transaction ID"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
                        />
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() =>
                            handleApprove(withdrawal.id, "approved")
                          }
                          disabled={processing === withdrawal.id}
                          className="flex-1 bg-success hover:bg-success-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:bg-gray-400"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Approve & Pay
                        </button>
                        <button
                          onClick={() =>
                            handleApprove(withdrawal.id, "rejected")
                          }
                          disabled={processing === withdrawal.id}
                          className="flex-1 bg-error hover:bg-error-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:bg-gray-400"
                        >
                          <XCircle className="w-5 h-5" />
                          Reject
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Payment reference display */}
                  {withdrawal.paymentReference && (
                    <div className="mt-4 bg-success-light rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">
                        Payment Reference
                      </p>
                      <p className="font-mono text-sm font-semibold text-gray-900">
                        {withdrawal.paymentReference}
                      </p>
                    </div>
                  )}

                  {/* Remarks display */}
                  {withdrawal.remarks && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Remarks</p>
                      <p className="text-sm text-gray-700">
                        {withdrawal.remarks}
                      </p>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <ArrowUpRight className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">
                No withdrawal requests
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {filter === "pending"
                  ? "No pending withdrawals to review"
                  : `No ${filter} withdrawals`}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminWithdrawals;
